import { useAppSelector } from 'hooks/redux';
import type { Collection, RecordOf } from 'immutable';
import React, { useCallback, useEffect } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography/Typography';

import ExpandableCard from 'components/ExpandableCard';
import HighlightableTypography from 'components/HighlightableTypography';
import AccountField from 'components/QuickenControls/AccountField';

import { accountsSelectors } from 'app-components/flux/accounts';
import {
  useField,
  useManager,
  useMultiFields,
  useSelector,
} from 'app-components/flux/transaction-rules/transactionRulesFormHooks';

import type { AccountProps } from 'app-components/flux/accounts/accountsTypes';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import type { AccountType } from 'app-components/flux/transaction-rules/transactionRulesTypes';

import IconButton from '@mui/material/IconButton/IconButton';
import { businessSelectors } from 'app-components/flux/business';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  addBtnRow: {
    display: 'flex',
    flex: 1,
    paddingBottom: theme.spacing(1),
    gap: theme.spacing(1),
    '> hr': {
      flex: 1,
      alignSelf: 'center',
    },
  },
  icon: {
    color: theme.palette.icon.light,
  },
  iconButton: {
    padding: 0,
  },
  card: {
    border: `1px ${theme.palette.border.light} solid`,
    background: theme.palette.background.default,
    '& .MuiCardHeader-root': {
      height: 52,
      borderBottom: `1px solid ${theme.palette.border.light}`,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    },
    marginTop: 0,
  },
  account: {
    '.MuiInputBase-root': {
      background: theme.palette.background.paper,
      input: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },
    },
    '.MuiInputLabel-shrink': {
      display: 'none',
    },
    '.MuiInputLabel-filled': {
      color: theme.palette.text.disabled,
    },
  },
  row: {
    display: 'flex',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  accountContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  buttonContainer: {
    display: 'flex',
    flex: 0,
  },
  errorText: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(1.5),
  },
}));

type AccountItemProps = {
  index: number;
  accounts:
    | Immutable.Map<string, Immutable.RecordOf<AccountProps>>
    | Collection<number, RecordOf<AccountProps>>
    | undefined;
};

const AccountItem = ({ index, accounts }: AccountItemProps) => {
  const { classes } = useStyles();
  const manager = useManager();
  const { fireAction } = manager;
  const { value, onValueChange, error } = useMultiFields('accountSourceIds', index);
  const formValue = useSelector(({ state }) => state.transactionRule.accountSource);
  const excludeIds =
    formValue?.accountSourceType === 'SPECIFIC' && 'accountSourceIds' in formValue ? formValue.accountSourceIds : [];

  const handleSelected = useCallback(
    (value: { id: string }) => {
      onValueChange(value.id);
    },
    [onValueChange],
  );

  return (
    <Box className={classes.row}>
      <FormControl className={classes.accountContainer}>
        <AccountField
          id='account-select'
          initialAccountId={value}
          accounts={accounts}
          onSelected={handleSelected}
          label='Account'
          variant='filled'
          margin='none'
          fullWidth
          className={classes.account}
          error={error}
          excludeIds={excludeIds}
        />
        {error && <FormHelperText className={classes.errorText}>Required</FormHelperText>}
      </FormControl>
      <Box className={classes.buttonContainer}>
        {excludeIds.length > 1 && (
          <CloseIcon
            id={`remove-payee-soure-${index}`}
            className={classes.icon}
            onClick={(_event) => fireAction('removeAccount', index)}
            sx={{ minHeight: 56 }}
          />
        )}
      </Box>
    </Box>
  );
};

type ConditionDividerProps = {
  button?: React.ReactNode;
  text: string;
};

const ConditionDivider = ({ button, text }: ConditionDividerProps) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.addBtnRow}>
      {button ? (
        <>
          <Divider />
          {button}
          <Typography variant='paragraphMedium' color='icon.light'>
            {text}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant='overlineMedium' color='icon.light'>
            {text}
          </Typography>
          <Divider />
        </>
      )}
    </Box>
  );
};

type QBPSectionProps = {
  accountType: string;
  onTypeChange: (value: string) => void;
};

const QBPSection = ({ accountType, onTypeChange }: QBPSectionProps) => {
  const { classes } = useStyles();
  const openBusinesses = useAppSelector(businessSelectors.getOpenBusinesses);

  // if account type is business and there are no open businesses, switch to personal
  useEffect(() => {
    if (openBusinesses.size === 0 && accountType === 'BUSINESS') {
      onTypeChange('PERSONAL');
    }
  }, [openBusinesses, accountType, onTypeChange]);

  return (
    <Box className={classes.row}>
      <FormControl>
        <RadioGroup
          aria-label='type'
          name='type'
          value={accountType}
          onChange={(_event, value) => {
            onTypeChange(value);
          }}
          sx={{ gap: 0.5 }}
        >
          {openBusinesses.size > 0 && (
            <FormControlLabel
              id='account-source-type-business'
              value='BUSINESS'
              control={<Radio sx={{ py: 0 }} />}
              label={
                <HighlightableTypography value={accountType} match='BUSINESS'>
                  All Business accounts
                </HighlightableTypography>
              }
            />
          )}
          <FormControlLabel
            id='account-source-type-personal'
            value='PERSONAL'
            control={<Radio sx={{ py: 0 }} />}
            label={
              <HighlightableTypography value={accountType} match='PERSONAL'>
                All Personal accounts
              </HighlightableTypography>
            }
          />
          <FormControlLabel
            id='account-source-type-specific'
            value='SPECIFIC'
            control={<Radio sx={{ py: 0 }} />}
            label={
              <HighlightableTypography value={accountType} match='SPECIFIC'>
                Specific accounts
              </HighlightableTypography>
            }
          />
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

const AccountTarget = () => {
  const { classes } = useStyles();
  const manager = useManager();
  const { fireAction } = manager;

  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const allAccounts: Immutable.Map<string, Immutable.RecordOf<AccountProps>> = useAppSelector(
    accountsSelectors.getOpenCashOrInvestmentAccounts,
  );
  const accountSource = useSelector(({ state }) => state.transactionRule.accountSource);

  const handleOnTypeChange = useCallback(
    (value: AccountType) => {
      manager.fireAction('setAccountType', value);
    },
    [manager],
  );

  if (!accountSource) {
    // ensure that a default account type is set in the form
    const defaultType = businessEnabled ? 'BUSINESS' : 'SPECIFIC';
    fireAction('setAccountType', defaultType);
    return null;
  }

  const hasEmpty =
    accountSource.accountSourceType === 'SPECIFIC' &&
    'accountSourceIds' in accountSource &&
    accountSource.accountSourceIds.includes('');

  return (
    <>
      {businessEnabled && (
        <>
          <QBPSection accountType={accountSource.accountSourceType} onTypeChange={handleOnTypeChange} />
        </>
      )}
      {/* Account selection */}
      {accountSource.accountSourceType === 'SPECIFIC' && (
        <>
          <Box>
            {accountSource.accountSourceIds.length === 0 ? (
              <AccountItem key={accountSource.accountSourceIds[0]} index={0} accounts={allAccounts} />
            ) : (
              accountSource.accountSourceIds?.map((_account: any, index: number, array: any[]) => {
                return (
                  <React.Fragment key={accountSource.accountSourceIds[index]}>
                    <AccountItem key={accountSource.accountSourceIds[index]} index={index} accounts={allAccounts} />
                    {index < array.length - 1 && <ConditionDivider text='OR' />}
                  </React.Fragment>
                );
              })
            )}
          </Box>

          {/* Add Account Button */}
          <ConditionDivider
            button={
              <IconButton className={classes.iconButton} disabled={hasEmpty}>
                <AddCircleIcon
                  id={`add-payee-source`}
                  className={classes.icon}
                  onClick={() => fireAction('addAccount', undefined)}
                />
              </IconButton>
            }
            text='OR'
          />
        </>
      )}
    </>
  );
};

export function AccountSourceCard(props: any) {
  const { classes, theme } = useStyles();
  const { value, onValueChange } = useField('isEnabledAccountSource');

  const toggleEnabled = () => {
    onValueChange(!value);
  };

  return (
    <ExpandableCard
      id='rule-account-source'
      className={classes.card}
      title='Account'
      cardHeaderProps={{ padding: `${theme.spacing(1.5)} ${theme.spacing(2)}` }}
      titleProps={{ variant: 'subtitle2' }}
      expandIcon={<Checkbox id='rule-account-source-enabled' checked={value} onClick={toggleEnabled}></Checkbox>}
      isExpanded={value || !!props.hideToggle}
      hideToggle={!!props.hideToggle}
    >
      <AccountTarget />
    </ExpandableCard>
  );
}
